import { useEffect } from 'react';

const makeDefaultGate = (id, label) => ({
  id,
  isComplete: false,
  label,
});

const makeDefaultStep = id => ({
  id,
  isComplete: false,
});

class StepGateController {
  static getStateDefaults() {
    return {
      currentGate: 0,
      currentStep: 0,
      gates: new Map(),
      isComplete: false,
      isError: false,
      steps: new Map(),
    };
  }

  constructor(setState) {
    this.setState = setState;

    /* Gate API Functionss */
    this.getStepApi = this.getStepApi.bind(this);
    this.registerGate = this.registerGate.bind(this);

    /* Step API Functions */
    this.goBackward = this.goBackward.bind(this);
    this.goForward = this.goForward.bind(this);
    this.goToGate = this.goToGate.bind(this);
    this.registerStep = this.registerStep.bind(this);
    this.setError = this.setError.bind(this);
  }

  getGateApi() {
    return {
      getStepApi: this.getStepApi,
      goToGate: this.goToGate,
      registerGate: this.registerGate,
      unregisterGate: this.unregisterGate,
    };
  }

  /*
    Gate API Functions
  */

  getStepApi() {
    return {
      goBackward: this.goBackward,
      goForward: this.goForward,
      goToGate: this.goToGate,
      registerStep: this.registerStep,
      setError: this.setError,
    };
  }

  addGate(gateId, label) {
    this.setState(state => ({
      ...state,
      gates: new Map(state.gates.set(gateId, makeDefaultGate(gateId, label))),
    }));
  }

  registerGate(gateId, label) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      this.addGate(gateId, label);
      return () => this.removeGate(gateId);
    }, [gateId, label]);
  }

  removeGate(gateId) {
    this.setState(state => {
      const { gates } = state;
      gates.delete(gateId);

      return {
        ...state,
        gates: new Map(state.gates.delete(gateId)),
      };
    });
  }

  /*
    Step API Functions
  */

  addStep(stepId) {
    this.setState(state => ({
      ...state,
      steps: new Map(state.steps.set(stepId, makeDefaultStep(stepId))),
    }));
  }

  goBackward() {
    setTimeout(() => {
      this.setState(state => {
        const { currentGate, currentStep, gates, steps } = state;

        let previousGate = currentGate;
        let previousStep = currentStep - 1;

        /* If there isn't a previous step, we'll go to the previous gate */
        if (previousStep < 0) {
          previousStep = 0;
          previousGate = currentGate - 1;

          /* If there isn't a previous gate, don't progress */
          if (previousGate < 0) {
            previousGate = currentGate;
          }
        }

        const [currentGateId, currentGateState] = [...gates][currentGate];
        const [previousGateId, previousGateState] = [...gates][previousGate];
        const [previousStepId, previousStepState] = [...steps][previousStep];
        const [currentStepId, currentStepState] = [...steps][currentStep];

        gates.set(currentGateId, { ...currentGateState, isComplete: false });
        gates.set(previousGateId, { ...previousGateState, isComplete: false });
        steps.set(currentStepId, {
          ...currentStepState,
          isComplete: false,
          isError: false,
        });
        steps.set(previousStepId, {
          ...previousStepState,
          isComplete: false,
          isError: false,
        });

        return {
          ...state,
          currentStep: previousStep,
          currentGate: previousGate,
          gates: new Map(gates),
          isComplete: false,
          steps: new Map(steps),
        };
      });
    }, 1);
  }

  goForward() {
    setTimeout(() => {
      this.setState(state => {
        const { currentGate, currentStep, gates, steps } = state;
        const [currentGateId, currentGateState] = [...gates][currentGate];
        const [currentStepId, currentStepState] = [...steps][currentStep];

        const gatesLength = [...gates].length;
        const stepsLength = [...steps].length;

        let nextGate = currentGate;
        let nextStep = currentStep + 1;
        let isComplete = false;
        let isGateComplete = false;

        /* If there isn't a next step, we'll go to the next gate */
        if (nextStep >= stepsLength) {
          nextStep = 0;
          nextGate = currentGate + 1;
          isGateComplete = true;

          /* If there isn't a next gate, don't progress */
          if (nextGate >= gatesLength) {
            nextStep = currentStep;
            nextGate = currentGate;
            isComplete = true;
          }
        }

        return {
          ...state,
          currentStep: nextStep,
          currentGate: nextGate,
          gates: new Map(
            gates.set(currentGateId, {
              ...currentGateState,
              isComplete: isGateComplete,
            }),
          ),
          isComplete,
          steps: new Map(
            steps.set(currentStepId, { ...currentStepState, isComplete: true }),
          ),
        };
      });
    }, 1);
  }

  goToGate(gateId) {
    setTimeout(() => {
      this.setState(state => {
        const { currentGate, gates } = state;
        const gateKeys = [...gates.keys()];
        const targetGate = gateKeys.indexOf(gateId);

        if (targetGate > currentGate) {
          throw new Error('Can only go back to a previous gate');
        }

        if (targetGate === -1) {
          throw new Error('Did not find target gate');
        }

        for (let i = targetGate; i <= currentGate; i += 1) {
          const key = gateKeys[i];
          gates.set(key, { ...gates.get(key), isComplete: false });
        }

        return {
          ...state,
          currentGate: targetGate,
          currentStep: 0,
          gates: new Map(gates),
          isComplete: false,
        };
      });
    }, 1);
  }

  registerStep(stepId) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      this.addStep(stepId);
      return () => this.removeStep(stepId);
    }, [stepId]);
  }

  removeStep(stepId) {
    this.setState(state => {
      const { steps } = state;
      steps.delete(stepId);

      return {
        ...state,
        steps: new Map(steps),
      };
    });
  }

  setError(isError) {
    this.setState(state => {
      const { currentStep, steps } = state;

      const [currentStepId, currentStepState] = [...steps][currentStep];

      steps.set(currentStepId, { ...currentStepState, isError });

      return {
        ...state,
        steps: new Map(steps),
      };
    });
  }
}

export default StepGateController;
